import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Menu, X, ArrowRight, ExternalLink } from 'lucide-react';
import WaitlistModal from '@/components/WaitlistModal';
import { Helmet } from 'react-helmet-async';
import '@/styles/Homepage.css';

const Homepage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post('https://contact-api.expertmarketingstrategy.com/api/contact', formData);
      toast.success((response.data && response.data.message) || 'Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  };

  return (
    <div className="homepage">
      <Helmet>
        <title>Making Complex Life Tasks Simple | Stratagease</title>
        <meta name="description" content="Strategy applications for businesses and consumers. Marketing Strategy App for micro businesses, Saivor for enjoyable food tracking with personalized recommendations for health and weight management, and Properly as a personal buyer agent to simplify home purchasing." />
        <meta property="og:title" content="Making Complex Life Tasks Simple | Stratagease" />
        <meta property="og:description" content="Strategy applications for businesses and consumers. Tools to simplify business growth, enjoyable eating for health challenges, and stress-free home buying." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="%PUBLIC_URL%/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="%PUBLIC_URL%/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Stratagease Ltd",
            "url": "https://seo-preview-5.preview.emergentagent.com",
            "logo": "https://customer-assets.emergentagent.com/job_port-strategy/artifacts/nbi8u7p3_Stratagease_logo_21%20Nov_25.png",
            "description": "Innovative strategy applications for small businesses. Tools designed to simplify business growth and operations.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "71-75 Shelton Street, Covent Garden",
              "addressLocality": "London",
              "postalCode": "WC2H 9JQ",
              "addressCountry": "GB"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "mail@stratagease.com",
              "contactType": "Customer Service"
            },
            "sameAs": [
              "https://www.instagram.com/stratagease",
              "https://www.facebook.com/stratagease",
              "https://www.twitter.com/stratagease",
              "https://www.linkedin.com/company/stratagease",
              "https://youtube.com/@marketing-strategy"
            ]
          })}
        </script>
      </Helmet>
      <WaitlistModal isOpen={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
      
      {/* Navigation */}
      <nav className="navbar" data-testid="navbar">
        <div className="nav-container">
          <a href="/" className="logo" data-testid="logo-link">
            <span className="logo-text">Stratag<span className="logo-green">ease</span></span>
          </a>
          
          <button 
            className="menu-toggle" 
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="menu-toggle"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className={`nav-links ${menuOpen ? 'active' : ''}`}>
            <button onClick={() => scrollToSection('products')} data-testid="nav-products">Solutions</button>
            <button onClick={() => scrollToSection('team')} data-testid="nav-team">Team</button>
            <button onClick={() => scrollToSection('contact')} data-testid="nav-contact">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">
            Making Complex Life Tasks <span className="gradient-text">Simple</span>
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Strategy apps that gives you certainty and confidence to tackle and control life's challenges with ease
          </p>
          <Button 
            onClick={() => scrollToSection('products')} 
            className="hero-cta"
            data-testid="hero-cta-button"
          >
            Get started <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
        <div className="hero-gradient"></div>
      </section>

      {/* Products Section */}
      <section id="products" className="products" data-testid="products-section">
        <h2 className="section-title" data-testid="products-title">Your Solutions</h2>
        <div className="products-grid">
          {/* Expert Marketing Strategy - NEW */}
          <div className="product-card straly" data-testid="product-card-straly">
            <div className="product-icon" data-testid="product-icon-straly">
              <img 
                src="https://customer-assets.emergentagent.com/job_71780dc9-4000-4948-b8ce-b0148f4c2021/artifacts/mk4lnwzl_icon.png" 
                alt="Expert Marketing Strategy" 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <h3 className="product-title" data-testid="product-title-straly">Expert Marketing Strategy</h3>
            <p className="product-tagline" data-testid="product-tagline-straly">No marketing experience? No problem.</p>
            <p className="product-description" data-testid="product-description-straly">
              Expert help, without the homework. No marketing experience required.
              <br /><br />
              Straly does the strategic thinking, creating, and the writing, you keep doing the work you're great at.
            </p>
            <div className="product-links">
              <a 
                href="https://play.google.com/store/apps/details?id=com.straly.app" 
                target="_blank" 
                rel="noopener noreferrer"
                className="store-link"
                data-testid="link-straly-google-play"
              >
                <ExternalLink size={16} /> Google Play
              </a>
              <a 
                href="https://apps.apple.com/app/straly/id6759396728" 
                target="_blank" 
                rel="noopener noreferrer"
                className="store-link"
                data-testid="link-straly-app-store"
              >
                <ExternalLink size={16} /> App Store
              </a>
            </div>
            <Button 
              onClick={() => window.open('https://expertmarketingstrategy.com', '_blank')}
              className="product-cta straly-cta"
              data-testid="cta-straly"
            >
              Visit Website
            </Button>
          </div>

          {/* Marketing Strategy App */}
          <div className="product-card marketing" data-testid="product-card-marketing">
            <div className="product-badge featured" data-testid="badge-featured">FEATURED</div>
            <div className="product-icon" data-testid="product-icon-marketing">
              <img 
                src="https://customer-assets.emergentagent.com/job_relaxed-lichterman/artifacts/ri9l0izh_Stratagease%20Logo%20Oct%2024.png" 
                alt="Marketing Strategy" 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <h3 className="product-title" data-testid="product-title-marketing">Marketing Strategy</h3>
            <p className="product-tagline" data-testid="product-tagline-marketing">Grow your micro business with confidence</p>
            <p className="product-description" data-testid="product-description-marketing">
              No marketing experience needed. Get personalized plans. +13% revenue proven.
            </p>
            <div className="product-links">
              <a 
                href="https://play.google.com/store/apps/details?id=com.app.stratagease" 
                target="_blank" 
                rel="noopener noreferrer"
                className="store-link"
                data-testid="link-google-play"
              >
                <ExternalLink size={16} /> Google Play
              </a>
              <a 
                href="https://apps.apple.com/us/app/marketing-strategy/id1634555279" 
                target="_blank" 
                rel="noopener noreferrer"
                className="store-link"
                data-testid="link-app-store"
              >
                <ExternalLink size={16} /> App Store
              </a>
            </div>
            <Button 
              onClick={() => window.location.href = '/marketing-strategy'}
              className="product-cta marketing-cta"
              data-testid="cta-marketing"
            >
              Learn More
            </Button>
          </div>

          {/* Saivor */}
          <div className="product-card saivor" data-testid="product-card-saivor">
            <div className="product-badge live" data-testid="badge-live">LIVE</div>
            <div className="product-icon" data-testid="product-icon-saivor">
              <img 
                src="https://customer-assets.emergentagent.com/job_relaxed-lichterman/artifacts/1pcwujl2_saivor_logo.png" 
                alt="Saivor" 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <h3 className="product-title" data-testid="product-title-saivor">Saivor</h3>
            <p className="product-tagline" data-testid="product-tagline-saivor">Eat without the stress</p>
            <p className="product-description" data-testid="product-description-saivor">
              Manage health conditions and weight. Personalized meal recommendations.
            </p>
            <Button 
              onClick={() => window.open('https://saivor.co/', '_blank')}
              className="product-cta saivor-cta"
              data-testid="cta-saivor"
            >
              Visit Website
            </Button>
          </div>

          {/* Properly */}
          <div className="product-card properly" data-testid="product-card-properly" style={{ display: 'none' }}>
            <div className="product-badge coming" data-testid="badge-coming">COMING EARLY 2026</div>
            <div className="product-icon" data-testid="product-icon-properly">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <h3 className="product-title" data-testid="product-title-properly">Properly</h3>
            <p className="product-tagline" data-testid="product-tagline-properly">Property, Properly Done — The Modern Way</p>
            <p className="product-description" data-testid="product-description-properly">
              Modern UK property transactions with AI legal checks and locked move dates.
            </p>
            <Button 
              onClick={() => window.location.href = '/properly'}
              className="product-cta properly-cta"
              data-testid="cta-properly"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners" data-testid="partners-section">
        <h2 className="section-title" data-testid="partners-title">In Partnership With</h2>
        <div className="partners-grid">
          <div className="partner-logo" data-testid="partner-google">
            <img 
              src="https://static.wixstatic.com/media/7cc6fc_04ec3065fd0b482cbd0ebf7968a7a37f~mv2.png/v1/crop/x_0,y_0,w_585,h_72/fill/w_308,h_38,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Google%20for%20Startups%20logo.png" 
              alt="Google for Startups"
            />
          </div>
          <div className="partner-logo" data-testid="partner-aws">
            <img 
              src="https://static.wixstatic.com/media/7cc6fc_82855199a82d41f8b9ef3bd83b006661~mv2.png/v1/fill/w_358,h_78,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/aws%20activate%20logo.png" 
              alt="AWS Activate"
            />
          </div>
          <div className="partner-logo" data-testid="partner-microsoft">
            <img 
              src="https://customer-assets.emergentagent.com/job_relaxed-lichterman/artifacts/ue5zqnty_Microsoft-for-Startups.jpg" 
              alt="Microsoft for Startups"
            />
          </div>
          <a 
            href="https://www.aiforge.org/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="partner-logo partner-link"
            data-testid="partner-aiforge"
          >
            <img 
              src="https://static.wixstatic.com/media/7cc6fc_b62fabe37150463ab6dee6b37709e9ec~mv2.jpeg/v1/crop/x_36,y_43,w_219,h_87/fill/w_250,h_96,al_c,lg_1,q_80,enc_avif,quality_auto/aiforge%20logo.jpeg" 
              alt="Aiforge"
            />
          </a>
          <a 
            href="https://elevenlabs.io/startup-grants" 
            target="_blank" 
            rel="noopener noreferrer"
            className="partner-logo partner-link"
            data-testid="partner-elevenlabs"
          >
            <img 
              src="https://eleven-public-cdn.elevenlabs.io/payloadcms/pwsc4vchsqt-ElevenLabsGrants.webp" 
              alt="ElevenLabs Startup Grants"
              style={{ width: '250px' }}
            />
          </a>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="team" data-testid="team-section">
        <h2 className="section-title" data-testid="team-title">Meet Our Team</h2>
        <p className="team-intro" data-testid="team-intro">
          We are a team of problem solvers, innovators, and doers, focused on helping people succeed.
        </p>
        <div className="team-grid">
          <div className="team-member" data-testid="team-member-raewyn">
            <div className="member-photo">
              <img 
                src="https://static.wixstatic.com/media/7cc6fc_a2ba49e1c5604a15969ad1d00cf55569~mv2.jpg/v1/fill/w_193,h_218,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Dr%20Raewyn%20Sleeman.jpg" 
                alt="Dr. Raewyn Sleeman"
                data-testid="photo-raewyn"
              />
            </div>
            <h3 className="member-name" data-testid="name-raewyn">Dr. Raewyn Sleeman (DBA)</h3>
            <p className="member-role" data-testid="role-raewyn">CEO, Founder</p>
            <div className="member-links">
              <a 
                href="https://www.linkedin.com/in/drraewynsleeman/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link linkedin-link"
                data-testid="linkedin-raewyn"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a 
                href="/tune-up-book" 
                className="social-link book-link"
                data-testid="book-raewyn"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
                <span>Buy Now</span>
              </a>
            </div>
          </div>

          <div className="team-member" data-testid="team-member-amit">
            <div className="member-photo">
              <img 
                src="https://customer-assets.emergentagent.com/job_71780dc9-4000-4948-b8ce-b0148f4c2021/artifacts/zpinwg6t_image.png" 
                alt="Amit Kumar"
                data-testid="photo-amit"
              />
            </div>
            <h3 className="member-name" data-testid="name-amit">Amit Kumar</h3>
            <p className="member-role" data-testid="role-amit">Mobile Application Developer</p>
            <div className="member-links">
              <a 
                href="https://www.linkedin.com/in/amit-kumar-40106b76/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link linkedin-link"
                data-testid="linkedin-amit"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="team-member" data-testid="team-member-jock">
            <div className="member-photo">
              <img 
                src="https://customer-assets.emergentagent.com/job_relaxed-lichterman/artifacts/w25cbd0d_Jock%20Chan.jpeg" 
                alt="Jock Chan"
                data-testid="photo-jock"
              />
            </div>
            <h3 className="member-name" data-testid="name-jock">Jock Chan</h3>
            <p className="member-role" data-testid="role-jock">GTM Angel (Investor)</p>
            <div className="member-links">
              <a 
                href="https://www.linkedin.com/in/jocks/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link linkedin-link"
                data-testid="linkedin-jock"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact" data-testid="contact-section">
        <h2 className="section-title" data-testid="contact-title">Get In Touch</h2>
        <p className="contact-subtitle" data-testid="contact-subtitle">
          Have questions? We'd love to hear from you.
        </p>
        <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
          <Input
            type="text"
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={handleInputChange}
            required
            data-testid="input-name"
          />
          <Input
            type="email"
            name="email"
            placeholder="Your Email"
            value={formData.email}
            onChange={handleInputChange}
            required
            data-testid="input-email"
          />
          <Textarea
            name="message"
            placeholder="Your Message"
            value={formData.message}
            onChange={handleInputChange}
            rows={5}
            required
            data-testid="textarea-message"
          />
          <Button 
            type="submit" 
            className="submit-btn" 
            disabled={submitting}
            data-testid="submit-button"
          >
            {submitting ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <p data-testid="footer-text">© 2025 Stratagease Ltd. Making complex life tasks simple.</p>
          <div className="footer-social" data-testid="footer-social">
            <a href="https://www.instagram.com/stratagease" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/stratagease" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="https://www.twitter.com/stratagease" target="_blank" rel="noopener noreferrer" aria-label="Twitter/X">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/stratagease" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a href="https://youtube.com/@marketing-strategy" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>
          <div className="footer-links">
            <a href="/privacy-policy" data-testid="footer-privacy">Privacy Policy</a>
            <a href="/terms-conditions" data-testid="footer-terms">Terms & Conditions</a>
            <a href="/disclaimer" data-testid="footer-disclaimer">Disclaimer</a>
            <a href="/return-policy" data-testid="footer-return">Return Policy</a>
            <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" data-testid="footer-google">Google Partner Technologies</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;